import { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";

import { DrawingContext, DrawingMode, DrawingFeatureType } from "../contexts/DrawingContext";
import { IgnitionRegimeContext } from "../contexts/IgnitionRegimeContext";
import { useAddIgnitionsBulk } from "../api/ignitionRegimes";
import FilestoreBrowser from "./FilestoreBrowser";
import { parseFilestoreFile, FilestoreFile } from "../api/filestore";

const AddIgnitionModal = () => {
  const { projectId } = useParams();
  const projectIdNumber = Number(projectId) || 0;

  const { selectedRegime } = useContext(IgnitionRegimeContext);
  const regimeId = selectedRegime?.id || 0;

  const { mutateAsync: addIgnitionsBulk } = useAddIgnitionsBulk(projectIdNumber, regimeId);

  const {
    drawingMode,
    setDrawingMode,
    drawingFeatureType,
    setDrawingFeatureType,
    geojson,
  } = useContext(DrawingContext);

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [errors, setErrors] = useState<{ name?: string }>({});

  // File upload state
  const [filestoreBrowserOpen, setFilestoreBrowserOpen] = useState(false);
  const [fileName, setFileName] = useState("");
  const [fileError, setFileError] = useState("");
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [uploadedGeojson, setUploadedGeojson] = useState<any>(null);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" && drawingMode === DrawingMode.IGNITION) {
      setOpen(true);
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [drawingMode]);

  // Auto-open modal when upload feature type is selected
  useEffect(() => {
    if (
      drawingMode === DrawingMode.IGNITION &&
      drawingFeatureType === DrawingFeatureType.UPLOAD
    ) {
      setOpen(true);
    }
  }, [drawingMode, drawingFeatureType]);

  const reset = () => {
    setName("");
    setErrors({});
    setDrawingMode(DrawingMode.NONE);
    setDrawingFeatureType(DrawingFeatureType.NONE);
    setFileName("");
    setFileError("");
    setUploadedGeojson(null);
    setIsFileLoading(false);
  };

  const handleFilestoreFileSelected = async (filestoreFile: FilestoreFile) => {
    setFileError("");
    setIsFileLoading(true);
    try {
      const geojson = await parseFilestoreFile(filestoreFile.id);
      setUploadedGeojson(geojson);
      setFileName(filestoreFile.original_filename);
      setFileError("");
    } catch (err) {
      console.error(err);
      setFileError("Failed to load file from filestore");
    } finally {
      setIsFileLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setOpen(false);
  };

  const validate = () => {
    const newErrors: { name?: string } = {};
    if (!name.trim()) {
      newErrors.name = "Ignition name is required";
    }
    return newErrors;
  };

  const isUploadFlow = drawingFeatureType === DrawingFeatureType.UPLOAD;

  const handleSubmit = async () => {
    const newErrors = validate();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      // Use uploaded geojson for upload flow, otherwise use drawn geojson from context
      const sourceGeojson = isUploadFlow ? uploadedGeojson : geojson;

      if (!sourceGeojson?.features?.length) {
        setErrors({ name: isUploadFlow ? "No file loaded" : "No geometry drawn" });
        return;
      }

      const baseName = name.trim();

      // Build array of ignitions for bulk API
      const ignitions = sourceGeojson.features
        .map((feature: any, index: number) => {
          const geometry = feature?.geometry;
          if (!geometry) {
            console.warn(`Feature ${index} has no geometry, skipping`);
            return null;
          }

          // Generate name: use base name + index for multiple features, or just base name for single
          const ignitionName = sourceGeojson.features.length > 1
            ? `${baseName} ${index + 1}`
            : baseName;

          return { geometry, name: ignitionName };
        })
        .filter(Boolean); // Remove null entries

      if (ignitions.length === 0) {
        setErrors({ name: "No valid geometries found" });
        return;
      }

      // Single bulk API call - much faster than individual requests
      await addIgnitionsBulk({ ignitions });
      handleClose();
    } catch (error) {
      console.error("Error adding ignition:", error);
      setErrors({ name: "Failed to add ignition(s). Please try again." });
    }
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#1e1e1e",
          borderRadius: 2,
          p: 3,
          minWidth: 300,
          maxWidth: 400,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, color: "white" }}>
          Add Ignition ({drawingFeatureType})
        </Typography>

        {/* Upload Section */}
        {isUploadFlow && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" sx={{ color: "gray", display: "block", mb: 1 }}>
              Shapefiles must be uploaded as a .zip archive.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setFilestoreBrowserOpen(true)}
                disabled={isFileLoading}
                sx={{
                  color: "#00d4ad",
                  borderColor: "#00d4ad",
                  "&:hover": {
                    borderColor: "#00b899",
                    backgroundColor: "rgba(0, 212, 173, 0.1)",
                  },
                }}
              >
                Browse My Files
              </Button>
              {fileName && (
                <Typography variant="body2" sx={{ color: "white", mt: 1 }}>
                  Selected: {fileName}
                </Typography>
              )}
            </Box>

            {fileError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {fileError}
              </Alert>
            )}

            {isFileLoading && (
              <Box sx={{ display: "flex", alignItems: "center", mt: 1, gap: 1 }}>
                <CircularProgress size={20} sx={{ color: "#00d4ad" }} />
                <Typography variant="body2" sx={{ color: "gray" }}>
                  Loading file...
                </Typography>
              </Box>
            )}

            <FilestoreBrowser
              open={filestoreBrowserOpen}
              onClose={() => setFilestoreBrowserOpen(false)}
              onSelectFile={handleFilestoreFileSelected}
              acceptedFileTypes=".zip,.kml,.geojson,.json"
            />
          </Box>
        )}

        <TextField
          fullWidth
          label="Ignition Name"
          name="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={!!errors.name}
          helperText={errors.name}
          sx={{
            mb: 2,
            "& .MuiInputBase-root": { color: "white" },
            "& .MuiInputLabel-root": { color: "gray" },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "gray" },
          }}
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button onClick={handleClose} sx={{ color: "gray" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{ backgroundColor: "#00d4ad" }}
          >
            Add
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddIgnitionModal;
